import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import DropdownArrow from '@assets/language-dropdown.svg';
import DropdownArrowDark from '@assets/language-dropdown-dark.svg';
import en from '@assets/languageFlags/en.png';
import hu from '@assets/languageFlags/hu.png';
import Triangle from '@assets/triangle.svg';
import { LanguageCode } from '@constants/languages';
import { SUPPORTED_LANGUAGES } from '@localization/intl-provider';
import { useUserStore } from '@store/use-user-store';

interface Locale {
  code: string;
  name: string;
}

interface Language {
  icon: string;
  languageTag: string;
  languageName: string;
}

interface LanguageDropdownProps {
  isLabelBlack?: boolean;
  locales?: Locale[];
}

const iconMap: Record<string, string> = {
  [LanguageCode.EN]: en,
  [LanguageCode.HU]: hu
};

const defaultLanguages: Language[] = [
  { icon: en, languageTag: LanguageCode.EN, languageName: 'English' },
  { icon: hu, languageTag: LanguageCode.HU, languageName: 'Hungarian' }
];

// A language is allowed if the dropdown offers it (configured locales, or the
// defaults when none are configured) and there are messages for it.
export const isAllowedLanguage = (lang: string, locales?: Locale[]) =>
  SUPPORTED_LANGUAGES.includes(lang) &&
  (locales?.length
    ? locales.some((locale) => locale.code === lang)
    : defaultLanguages.some((x) => x.languageTag === lang));

const Container = styled.div`
  position: relative;
`;

const MenuSelect = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  min-width: 160px;
  justify-content: space-between;
  @media only screen and (max-width: 992px) {
    min-width: unset;
  }
`;

const LanguageFlagImg = styled.img`
  margin-right: 0.5em;
`;

const LanguageContext = styled.div`
  display: flex;
  align-items: center;
`;

interface LabelProps {
  isLabelBlack?: boolean;
}

const LanguageText = styled.span<LabelProps>`
  margin-right: 0.5em;
  font-size: 20px;
  font-weight: 500;
  color: ${({ theme, isLabelBlack }) => (isLabelBlack ? theme.primary : theme.white)};
  font-family: 'Urbanist', sans-serif;
`;

const Menu = styled.div`
  position: absolute;
  top: 60px;
  background: ${({ theme }) => theme.white};
  padding: 20px 0;
  border-radius: 16px;
  box-shadow: 1px 1px 15px -2px rgb(0 0 0 / 75%);
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 16px;
  height: 50px;
  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
  }
`;

interface SelectableProps {
  selected?: boolean;
  isBorder?: boolean;
}

const SelectedBorderContainer = styled.div<SelectableProps>`
  width: 4px;
  height: 100%;
  margin-right: 0.5em;
  display: flex;
  align-items: center;
  padding-bottom: ${(props) => (props.isBorder && props.selected ? '16px' : '')};
`;

const SelectedBorder = styled.div<SelectableProps>`
  width: 100%;
  height: 25px;
  background: ${({ theme, selected }) => (selected ? theme.primary : '')};
`;

const MenuContent = styled.div<SelectableProps>`
  display: flex;
  margin-right: 0.5em;
  width: 100%;
  border-bottom: ${({ theme, isBorder }) => (isBorder ? `1px solid ${theme.border}` : '')};
  padding-bottom: ${(props) => (props.isBorder ? '16px' : '')};
  align-items: center;
  cursor: pointer;
`;

const TriangleImg = styled.img`
  position: absolute;
  top: -8px;
  right: 24px;
`;

const LanguageMenuText = styled.span`
  font-family: 'Urbanist', sans-serif;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.primary};
`;

function LanguageDropdown({ isLabelBlack, locales }: LanguageDropdownProps) {
  const { selectedLang, changeLanguage } = useUserStore();

  const languages = useMemo<Language[]>(() => {
    if (locales?.length) {
      return locales.map((locale) => ({
        icon: iconMap[locale.code],
        languageTag: locale.code,
        languageName: locale.name
      }));
    }
    return defaultLanguages;
  }, [locales]);

  const [selectedLanguage, setSelectedLanguage] = useState<Language>(
    languages.find((x) => x.languageTag === selectedLang) ?? languages[0]
  );
  const [menu, setMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useMemo(() => {
    setSelectedLanguage(
      languages.find((x) => x.languageTag === selectedLang) ?? languages[0]
    );
  }, [selectedLang, languages]);

  const languageChanger = (lang: string) => {
    changeLanguage(lang);
  };

  function useOutsideClickHandler(ref: React.RefObject<HTMLDivElement | null>) {
    useEffect(() => {
      function handleClickOutside(event: MouseEvent) {
        if (ref.current && !ref.current.contains(event.target as Node)) {
          setMenu(false);
        }
      }
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [ref]);
  }

  useOutsideClickHandler(menuRef);

  return (
    <Container>
      <MenuSelect onClick={() => setMenu(true)}>
        <LanguageContext>
          <LanguageFlagImg
            src={selectedLanguage.icon}
            alt={selectedLanguage.languageTag}
          />
          <LanguageText isLabelBlack={isLabelBlack}>
            {selectedLanguage.languageName}
          </LanguageText>
        </LanguageContext>
        <img
          src={isLabelBlack ? DropdownArrowDark : DropdownArrow}
          alt="dropdown-arrow"
        />
      </MenuSelect>
      {menu && (
        <Menu ref={menuRef}>
          <TriangleImg src={Triangle} alt={'triangle'} />
          {languages.map((lang, index) => (
            <MenuItem
              key={index}
              onClick={() => languageChanger(lang.languageTag)}
            >
              <SelectedBorderContainer
                selected={selectedLanguage.languageTag === lang.languageTag}
                isBorder={index < languages.length - 1}
              >
                <SelectedBorder
                  selected={selectedLanguage.languageTag === lang.languageTag}
                />
              </SelectedBorderContainer>
              <MenuContent isBorder={index < languages.length - 1}>
                <LanguageFlagImg src={lang.icon} alt={lang.languageTag} />
                <LanguageMenuText>{lang.languageName}</LanguageMenuText>
              </MenuContent>
            </MenuItem>
          ))}
        </Menu>
      )}
    </Container>
  );
}

export default LanguageDropdown;
